import { useState, useEffect, useCallback } from 'react';
import { hospitalApi } from '../../hospitalApi';

const C = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';

const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ROLES = ['Nurse', 'Receptionist', 'Lab Technician', 'Cleaner', 'Pharmacist', 'Security', 'Admin', 'Other'];

const EMPTY = {
  name: '', role: 'Nurse', phone: '', email: '',
  salary: '', joinDate: '', status: 'active', notes: '',
};

const inp = { className: 'form-control' };

function F({ label, children }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

export default function HospitalStaff() {
  const [staff, setStaff]     = useState([]);
  const [form, setForm]       = useState(EMPTY);
  const [editId, setEditId]   = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');

  const load = useCallback(() => {
    hospitalApi.getStaff().then(d => { if (d) setStaff(d); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm(EMPTY); setEditId(null); setErr(''); };

  const startEdit = (s) => {
    setForm({
      name: s.name || '', role: s.role || 'Nurse', phone: s.phone || '',
      email: s.email || '', salary: s.salary != null ? String(s.salary) : '',
      joinDate: s.join_date || '', status: s.status || 'active', notes: s.notes || '',
    });
    setEditId(s.id);
    setShowForm(true);
    setErr('');
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...form, salary: parseFloat(form.salary) || 0 };
      if (editId) await hospitalApi.updateStaff(editId, payload);
      else await hospitalApi.createStaff(payload);
      load();
      setShowForm(false);
      resetForm();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this staff member?')) return;
    await hospitalApi.deleteStaff(id);
    load();
  };

  const totalSalary = staff.reduce((s, m) => s + (Number(m.salary) || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: DARK }}>
          👩‍⚕️ Hospital Staff ({staff.length})
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#5f6368' }}>
            Monthly Salaries: <strong style={{ color: '#c5221f' }}>{fmtRs(totalSalary)}</strong>
          </span>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowForm(!showForm); }}>
            {showForm && !editId ? '✕ Cancel' : '+ Add Staff'}
          </button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header">
            <div className="card-title">{editId ? 'Edit Staff Member' : 'Add Staff Member'}</div>
            <button style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#5f6368' }}
              onClick={() => { setShowForm(false); resetForm(); }}>×</button>
          </div>
          <div className="card-body">
            {err && <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 8, padding: '8px 12px', marginBottom: 14, fontSize: 13 }}>⚠️ {err}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              <F label="Full Name *">
                <input {...inp} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </F>
              <F label="Role">
                <select {...inp} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </F>
              <F label="Phone">
                <input {...inp} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </F>
              <F label="Email">
                <input {...inp} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </F>
              <F label="Monthly Salary (₹)">
                <input {...inp} type="number" min={0} value={form.salary}
                  onChange={e => setForm(f => ({ ...f, salary: e.target.value }))} />
              </F>
              <F label="Join Date">
                <input {...inp} type="date" value={form.joinDate}
                  onChange={e => setForm(f => ({ ...f, joinDate: e.target.value }))} />
              </F>
              <F label="Status">
                <select {...inp} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </F>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label className="form-label">Notes</label>
                <textarea {...inp} rows={2} value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? '💾 Update' : '➕ Add Staff'}
              </button>
              <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
                background: '#fff', cursor: 'pointer', fontSize: 13 }}
                onClick={() => { setShowForm(false); resetForm(); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Staff table */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {staff.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9aa0a6' }}>No active staff members</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: LIGHT }}>
                  {['Name', 'Role', 'Phone', 'Email', 'Salary', 'Join Date', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {staff.map((s, i) => (
                  <tr key={s.id} style={{ borderTop: '1px solid #f1f3f4', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: DARK }}>{s.name}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ background: LIGHT, color: C, borderRadius: 5,
                        padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                        {s.role}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', color: '#5f6368' }}>{s.phone || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#5f6368', fontSize: 12 }}>{s.email || '—'}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#27ae60' }}>
                      {s.salary > 0 ? fmtRs(s.salary) : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#5f6368' }}>{s.join_date || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        background: s.status === 'active' ? '#e6f4ea' : '#f5f5f5',
                        color: s.status === 'active' ? '#27ae60' : '#888',
                        borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700
                      }}>{s.status}</span>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={{ fontSize: 11, padding: '3px 8px', background: LIGHT,
                          color: DARK, border: `1px solid ${C}`, borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                          onClick={() => startEdit(s)}>Edit</button>
                        <button style={{ fontSize: 11, padding: '3px 8px', background: '#fce8e6',
                          color: '#c5221f', border: '1px solid #f5c6c6', borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                          onClick={() => handleDelete(s.id)}>Deactivate</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: LIGHT, borderTop: '2px solid #d0e8f7' }}>
                  <td colSpan={4} style={{ padding: '10px 12px', fontWeight: 800, color: DARK }}>
                    Monthly Salary Total
                  </td>
                  <td style={{ padding: '10px 12px', fontWeight: 800, color: '#c5221f', fontSize: 15 }}>
                    {fmtRs(totalSalary)}
                  </td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
